import React, { useState, useEffect, useCallback } from 'react';
import { CreditCard, RefreshCw, CheckCircle, AlertTriangle, Calendar, FileText, Pencil, Trash2, Plus, XCircle, Coins, BarChart3 } from 'lucide-react';
import './PaymentSchedule.css';

const PaymentSchedule = ({ clientId, token, isAdmin }) => {
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEcheance, setEditingEcheance] = useState(null);
  const [generateForm, setGenerateForm] = useState({
    nombre_echeances: 3,
    date_premiere_echeance: new Date().toISOString().split('T')[0]
  });
  const [addForm, setAddForm] = useState({
    montant: '',
    date_prevue: new Date().toISOString().split('T')[0],
    notes: ''
  });

  const fetchSchedule = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/clients/${clientId}/payment-schedule`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSchedule(data);
      }
    } catch (error) {
      console.error('Erreur chargement échéancier:', error);
    } finally {
      setLoading(false);
    }
  }, [clientId, token]);

  useEffect(() => {
    if (clientId && token) {
      fetchSchedule();
    }
  }, [clientId, token, fetchSchedule]);

  const handleGenerateSchedule = async () => {
    try {
      const response = await fetch(`/api/clients/${clientId}/generate-schedule`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(generateForm)
      });

      if (response.ok) {
        setShowGenerateModal(false);
        fetchSchedule();
      } else {
        const error = await response.json();
        alert(error.error || 'Erreur lors de la génération');
      }
    } catch (error) {
      console.error('Erreur génération:', error);
      alert('Erreur lors de la génération de l\'échéancier');
    }
  };

  const handleAddEcheance = async () => {
    try {
      const response = await fetch(`/api/clients/${clientId}/payment-schedule`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(addForm)
      });

      if (response.ok) {
        setShowAddModal(false);
        setAddForm({ montant: '', date_prevue: new Date().toISOString().split('T')[0], notes: '' });
        fetchSchedule();
      } else {
        const error = await response.json();
        alert(error.error || 'Erreur lors de l\'ajout');
      }
    } catch (error) {
      console.error('Erreur ajout:', error);
    }
  };

  const handleMarkPaid = async (echeanceId, datePaiement = null) => {
    try {
      const response = await fetch(`/api/payment-schedule/${echeanceId}/mark-paid`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ date_paiement: datePaiement })
      });

      if (response.ok) {
        const result = await response.json();
        fetchSchedule();
        if (result.commission_agent > 0 || result.commission_commercial > 0) {
          alert(`Paiement enregistré !\nCommission agent: ${result.commission_agent}€\nCommission commercial: ${result.commission_commercial}€`);
        }
      } else {
        const error = await response.json();
        alert(error.error || 'Erreur');
      }
    } catch (error) {
      console.error('Erreur marquage payé:', error);
    }
  };

  const handleMarkUnpaid = async (echeanceId) => {
    if (!window.confirm('Annuler ce paiement ? La commission associée sera également supprimée.')) {
      return;
    }

    try {
      const response = await fetch(`/api/payment-schedule/${echeanceId}/mark-unpaid`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (response.ok) {
        fetchSchedule();
      } else {
        const error = await response.json();
        alert(error.error || 'Erreur');
      }
    } catch (error) {
      console.error('Erreur annulation:', error);
    }
  };

  const handleEditEcheance = async () => {
    try {
      const response = await fetch(`/api/payment-schedule/${editingEcheance.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          montant: editingEcheance.montant,
          date_prevue: editingEcheance.date_prevue,
          notes: editingEcheance.notes
        })
      });

      if (response.ok) {
        setShowEditModal(false);
        setEditingEcheance(null);
        fetchSchedule();
      } else {
        const error = await response.json();
        alert(error.error || 'Erreur');
      }
    } catch (error) {
      console.error('Erreur modification:', error);
    }
  };

  const handleDeleteEcheance = async (echeanceId) => {
    if (!window.confirm('Supprimer cette échéance ?')) return;

    try {
      const response = await fetch(`/api/payment-schedule/${echeanceId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        fetchSchedule();
      } else {
        const error = await response.json();
        alert(error.error || 'Erreur');
      }
    } catch (error) {
      console.error('Erreur suppression:', error);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatMontant = (montant) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(montant || 0);
  };

  if (loading) {
    return (
      <div className="payment-schedule">
        <div className="loading-schedule">
          <div className="spinner"></div>
          <p>Chargement de l'échéancier...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-schedule">
      <h3>
        <span className="icon"><CreditCard size={20} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /></span>
        Échéancier de Paiement
      </h3>

      {/* Résumé */}
      {schedule && (
        <>
          <div className="payment-summary">
            <div className="summary-item">
              <div className="label">Bon de commande</div>
              <div className="value total">{formatMontant(schedule.montant_bon_commande)}</div>
            </div>
            <div className="summary-item">
              <div className="label">Total payé</div>
              <div className="value paye">{formatMontant(schedule.total_paye)}</div>
            </div>
            <div className="summary-item">
              <div className="label">Reste à payer</div>
              <div className="value reste">{formatMontant(schedule.reste_a_payer)}</div>
            </div>
            <div className="summary-item">
              <div className="label">Progression</div>
              <div className="value">{schedule.pourcentage_paye}%</div>
            </div>
          </div>

          {/* Barre de progression */}
          <div className="progress-bar-container">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${schedule.pourcentage_paye}%` }}
              ></div>
              {schedule.pourcentage_paye > 10 && (
                <span className="progress-text">{schedule.pourcentage_paye}%</span>
              )}
            </div>
          </div>
        </>
      )}

      {/* Actions */}
      <div className="schedule-actions">
        <button className="btn-generate" onClick={() => setShowGenerateModal(true)}>
          <span><RefreshCw size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /></span> Générer échéancier
        </button>
        <button className="btn-add" onClick={() => setShowAddModal(true)}>
          <span>+</span> Ajouter échéance
        </button>
      </div>

      {/* Liste des échéances */}
      {schedule?.echeances?.length > 0 ? (
        <div className="echeances-list">
          {schedule.echeances.map((echeance) => (
            <div key={echeance.id} className={`echeance-item ${echeance.statut}`}>
              <div className="echeance-numero">{echeance.echeance_numero}</div>
              <div className="echeance-details">
                <div className="echeance-montant">{formatMontant(echeance.montant)}</div>
                <div className={`echeance-date ${echeance.statut === 'paye' ? 'payee' : ''} ${echeance.statut === 'en_retard' ? 'retard' : ''}`}>
                  {echeance.statut === 'paye' ? (
                    <><CheckCircle size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Payé le {formatDate(echeance.date_paiement)}</>
                  ) : echeance.statut === 'en_retard' ? (
                    <><AlertTriangle size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> En retard depuis le {formatDate(echeance.date_prevue)}</>
                  ) : (
                    <><Calendar size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Prévu le {formatDate(echeance.date_prevue)}</>
                  )}
                </div>
                {echeance.notes && (
                  <div className="echeance-notes" style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                    <FileText size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> {echeance.notes}
                  </div>
                )}
              </div>
              <span className={`echeance-status ${echeance.statut}`}>
                {echeance.statut === 'paye' ? 'Payé' : echeance.statut === 'en_retard' ? 'En retard' : 'En attente'}
              </span>
              <div className="echeance-actions">
                {echeance.statut !== 'paye' ? (
                  <>
                    <button
                      className="btn-payer"
                      onClick={() => handleMarkPaid(echeance.id)}
                    >
                      ✓ Marquer payé
                    </button>
                    <button
                      className="btn-modifier"
                      onClick={() => {
                        setEditingEcheance({
                          ...echeance,
                          date_prevue: echeance.date_prevue?.split('T')[0]
                        });
                        setShowEditModal(true);
                      }}
                    >
                      <Pencil size={14} style={{display:'inline',verticalAlign:'middle'}} />
                    </button>
                    {isAdmin && (
                      <button
                        className="btn-annuler"
                        onClick={() => handleDeleteEcheance(echeance.id)}
                        title="Supprimer"
                      >
                        <Trash2 size={14} style={{display:'inline',verticalAlign:'middle'}} />
                      </button>
                    )}
                  </>
                ) : (
                  isAdmin && (
                    <button
                      className="btn-annuler"
                      onClick={() => handleMarkUnpaid(echeance.id)}
                    >
                      ✗ Annuler
                    </button>
                  )
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="no-echeances">
          <div className="icon">📋</div>
          <p>Aucune échéance définie. Cliquez sur "Générer échéancier" pour créer automatiquement les échéances.</p>
        </div>
      )}

      {/* Modal Générer échéancier */}
      {showGenerateModal && (
        <div className="generate-modal-overlay">
          <div className="generate-modal">
            <h4>Générer l'échéancier</h4>
            <div className="form-group">
              <label>Nombre d'échéances</label>
              <input
                type="number"
                min="1"
                max="24"
                value={generateForm.nombre_echeances}
                onChange={(e) => setGenerateForm({ ...generateForm, nombre_echeances: parseInt(e.target.value) })}
              />
            </div>
            <div className="form-group">
              <label>Date de la première échéance</label>
              <input
                type="date"
                value={generateForm.date_premiere_echeance}
                onChange={(e) => setGenerateForm({ ...generateForm, date_premiere_echeance: e.target.value })}
              />
            </div>
            <p style={{ fontSize: '0.85rem', color: '#6c757d', marginTop: '10px' }}>
              Les échéances seront espacées d'un mois chacune.
              {schedule?.montant_bon_commande && generateForm.nombre_echeances > 0 && (
                <><br />Montant par échéance : <strong>{formatMontant(schedule.montant_bon_commande / generateForm.nombre_echeances)}</strong></>
              )}
            </p>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setShowGenerateModal(false)}>
                Annuler
              </button>
              <button className="btn-confirm" onClick={handleGenerateSchedule}>
                Générer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Ajouter échéance */}
      {showAddModal && (
        <div className="generate-modal-overlay">
          <div className="generate-modal">
            <h4>Ajouter une échéance</h4>
            <div className="form-group">
              <label>Montant (€)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={addForm.montant}
                onChange={(e) => setAddForm({ ...addForm, montant: e.target.value })}
                placeholder="350.00"
              />
            </div>
            <div className="form-group">
              <label>Date prévue</label>
              <input
                type="date"
                value={addForm.date_prevue}
                onChange={(e) => setAddForm({ ...addForm, date_prevue: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Notes (optionnel)</label>
              <input
                type="text"
                value={addForm.notes}
                onChange={(e) => setAddForm({ ...addForm, notes: e.target.value })}
                placeholder="Ex: Paiement par chèque"
              />
            </div>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setShowAddModal(false)}>
                Annuler
              </button>
              <button className="btn-confirm" onClick={handleAddEcheance}>
                Ajouter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Modifier échéance */}
      {showEditModal && editingEcheance && (
        <div className="generate-modal-overlay">
          <div className="generate-modal">
            <h4>Modifier l'échéance #{editingEcheance.echeance_numero}</h4>
            <div className="form-group">
              <label>Montant (€)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={editingEcheance.montant}
                onChange={(e) => setEditingEcheance({ ...editingEcheance, montant: e.target.value })}
                disabled={editingEcheance.statut === 'paye'}
              />
            </div>
            <div className="form-group">
              <label>Date prévue</label>
              <input
                type="date"
                value={editingEcheance.date_prevue}
                onChange={(e) => setEditingEcheance({ ...editingEcheance, date_prevue: e.target.value })}
                disabled={editingEcheance.statut === 'paye'}
              />
            </div>
            <div className="form-group">
              <label>Notes</label>
              <input
                type="text"
                value={editingEcheance.notes || ''}
                onChange={(e) => setEditingEcheance({ ...editingEcheance, notes: e.target.value })}
              />
            </div>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setShowEditModal(false)}>
                Annuler
              </button>
              <button className="btn-confirm" onClick={handleEditEcheance}>
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentSchedule;
